import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { api } from '../../lib/api';
import { isoToDateInput, parseDateToIso, parseEuroToCentimes } from '../../lib/format';
import type { ModePaiement } from '@cockpit/shared';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  venteId: number;
  echeanceId?: number | null;
  defaultMontantCentimes?: number;
}

export function PaiementClientModal({
  isOpen,
  onClose,
  venteId,
  echeanceId,
  defaultMontantCentimes,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(isoToDateInput(new Date().toISOString()));
  const [montant, setMontant] = useState(
    defaultMontantCentimes !== undefined
      ? (defaultMontantCentimes / 100).toFixed(2).replace('.', ',')
      : '',
  );
  const [mode, setMode] = useState<ModePaiement>('Virement');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const dateIso = parseDateToIso(date);
      const montantCt = parseEuroToCentimes(montant);
      if (dateIso === null) throw new Error('Date invalide');
      if (montantCt === null || montantCt <= 0) throw new Error('Montant invalide');
      return api.paiementsClients.create({
        vente_id: venteId,
        echeance_id: echeanceId ?? null,
        date_effective: dateIso,
        montant_centimes: montantCt,
        mode_paiement: mode,
        notes: notes === '' ? null : notes,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['paiementsClients', venteId] });
      void queryClient.invalidateQueries({ queryKey: ['echeances', venteId] });
      void queryClient.invalidateQueries({ queryKey: ['vente', venteId] });
      void queryClient.invalidateQueries({ queryKey: ['encaissements'] });
      void queryClient.invalidateQueries({ queryKey: ['encaissements-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-tresorerie'] });
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Erreur');
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Enregistrer un paiement"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={mutation.isPending}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Date du paiement"
          type="date"
          value={date}
          onChange={(e): void => setDate(e.target.value)}
        />
        <Input
          label="Montant (€)"
          value={montant}
          onChange={(e): void => setMontant(e.target.value)}
          placeholder="0,00"
        />
        <Select
          label="Mode de paiement"
          value={mode}
          onChange={(e): void => setMode(e.target.value as ModePaiement)}
          options={[
            { value: 'Virement', label: 'Virement' },
            { value: 'Cheque', label: 'Chèque' },
            { value: 'Especes', label: 'Espèces' },
          ]}
        />
        <Input label="Notes" value={notes} onChange={(e): void => setNotes(e.target.value)} />
        {error !== null && <p className="text-xs text-red-400">{error}</p>}
      </div>
    </Modal>
  );
}
